import { gzip, gunzip } from 'zlib';
import { promisify } from 'util';
import chalk from 'chalk';
import ora from 'ora';

const gzipAsync = promisify(gzip);
const gunzipAsync = promisify(gunzip);

/**
 * 缓存压缩和序列化优化器
 * 提供多种压缩算法和序列化策略
 */
export class CacheCompressor {
  private compressionLevel: number = 6; // 默认压缩级别
  private spinner = ora();

  constructor(compressionLevel: number = 6) {
    this.compressionLevel = compressionLevel;
  }

  /**
   * 压缩缓存数据
   */
  async compress(
    data: any,
    algorithm: 'gzip' | 'brotli' | 'none' = 'gzip'
  ): Promise<Buffer> {
    const serialized = this.serialize(data);

    switch (algorithm) {
      case 'gzip':
        return await this.compressGzip(serialized);
      case 'brotli':
        return await this.compressBrotli(serialized);
      case 'none':
        return Buffer.from(serialized);
      default:
        throw new Error(`不支持的压缩算法: ${algorithm}`);
    }
  }

  /**
   * 解压缩缓存数据
   */
  async decompress(
    buffer: Buffer,
    algorithm: 'gzip' | 'brotli' | 'none' = 'gzip'
  ): Promise<any> {
    let decompressed: Buffer;

    switch (algorithm) {
      case 'gzip':
        decompressed = await this.decompressGzip(buffer);
        break;
      case 'brotli':
        decompressed = await this.decompressBrotli(buffer);
        break;
      case 'none':
        decompressed = buffer;
        break;
      default:
        throw new Error(`不支持的压缩算法: ${algorithm}`);
    }

    return this.deserialize(decompressed.toString());
  }

  /**
   * 序列化数据
   */
  serialize(data: any): string {
    try {
      // 使用 JSON.stringify 进行序列化
      // 可以考虑使用更高效的序列化库如 msgpack
      return JSON.stringify(data);
    } catch (error) {
      console.error(chalk.red('序列化失败:'), error);
      throw error;
    }
  }

  /**
   * 反序列化数据
   */
  deserialize(serialized: string): any {
    try {
      return JSON.parse(serialized);
    } catch (error) {
      console.error(chalk.red('反序列化失败:'), error);
      throw error;
    }
  }

  /**
   * 计算压缩比
   */
  calculateCompressionRatio(
    originalSize: number,
    compressedSize: number
  ): number {
    return originalSize > 0
      ? (originalSize - compressedSize) / originalSize
      : 0;
  }

  /**
   * 选择最佳压缩算法
   */
  async selectBestCompression(data: any): Promise<{
    algorithm: 'gzip' | 'brotli' | 'none';
    compressed: Buffer;
    ratio: number;
  }> {
    this.spinner.start('选择最佳压缩算法...');

    try {
      const serialized = this.serialize(data);

      // 测试不同压缩算法
      const results = await Promise.all([
        this.testCompression(serialized, 'gzip'),
        this.testCompression(serialized, 'brotli'),
        this.testCompression(serialized, 'none'),
      ]);

      // 选择压缩比最高的算法
      const bestResult = results.reduce((best, current) =>
        current.ratio > best.ratio ? current : best
      );

      this.spinner.succeed(
        `选择压缩算法: ${bestResult.algorithm} (压缩比: ${(bestResult.ratio * 100).toFixed(2)}%)`
      );
      return bestResult;
    } catch (error) {
      this.spinner.fail('选择压缩算法失败');
      console.error(chalk.red('压缩错误:'), error);
      throw error;
    }
  }

  /**
   * 批量压缩
   */
  async compressBatch(
    items: Array<{ key: string; data: any }>,
    algorithm: 'gzip' | 'brotli' | 'none' = 'gzip'
  ): Promise<Map<string, Buffer>> {
    this.spinner.start(`批量压缩 ${items.length} 项数据...`);

    try {
      const compressedItems = new Map<string, Buffer>();

      // 并行压缩
      const promises = items.map(async item => {
        const compressed = await this.compress(item.data, algorithm);
        return { key: item.key, compressed };
      });

      const results = await Promise.all(promises);

      for (const result of results) {
        compressedItems.set(result.key, result.compressed);
      }

      this.spinner.succeed(`批量压缩完成 (${items.length} 项)`);
      return compressedItems;
    } catch (error) {
      this.spinner.fail('批量压缩失败');
      console.error(chalk.red('批量压缩错误:'), error);
      throw error;
    }
  }

  /**
   * 批量解压缩
   */
  async decompressBatch(
    items: Map<string, Buffer>,
    algorithm: 'gzip' | 'brotli' | 'none' = 'gzip'
  ): Promise<Map<string, any>> {
    this.spinner.start(`批量解压缩 ${items.size} 项数据...`);

    try {
      const decompressedItems = new Map<string, any>();

      // 并行解压缩
      const promises = Array.from(items.entries()).map(
        async ([key, buffer]) => {
          const decompressed = await this.decompress(buffer, algorithm);
          return { key, decompressed };
        }
      );

      const results = await Promise.all(promises);

      for (const result of results) {
        decompressedItems.set(result.key, result.decompressed);
      }

      this.spinner.succeed(`批量解压缩完成 (${items.size} 项)`);
      return decompressedItems;
    } catch (error) {
      this.spinner.fail('批量解压缩失败');
      console.error(chalk.red('批量解压缩错误:'), error);
      throw error;
    }
  }

  /**
   * 测试压缩算法
   */
  private async testCompression(
    data: string,
    algorithm: 'gzip' | 'brotli' | 'none'
  ): Promise<{
    algorithm: 'gzip' | 'brotli' | 'none';
    compressed: Buffer;
    ratio: number;
  }> {
    const originalSize = Buffer.byteLength(data);
    let compressed: Buffer;

    switch (algorithm) {
      case 'gzip':
        compressed = await this.compressGzip(data);
        break;
      case 'brotli':
        compressed = await this.compressBrotli(data);
        break;
      case 'none':
        compressed = Buffer.from(data);
        break;
    }

    const ratio = this.calculateCompressionRatio(
      originalSize,
      compressed.length
    );

    return {
      algorithm,
      compressed,
      ratio,
    };
  }

  /**
   * Gzip 压缩
   */
  private async compressGzip(data: string): Promise<Buffer> {
    return await gzipAsync(Buffer.from(data), { level: this.compressionLevel });
  }

  /**
   * Gzip 解压缩
   */
  private async decompressGzip(buffer: Buffer): Promise<Buffer> {
    return await gunzipAsync(buffer);
  }

  /**
   * Brotli 压缩
   */
  private async compressBrotli(data: string): Promise<Buffer> {
    // 需要安装 brotli 包
    try {
      const brotli = require('brotli');
      return Buffer.from(brotli.compress(Buffer.from(data)));
    } catch (error) {
      console.warn(chalk.yellow('Brotli 压缩不可用，回退到 Gzip'));
      return await this.compressGzip(data);
    }
  }

  /**
   * Brotli 解压缩
   */
  private async decompressBrotli(buffer: Buffer): Promise<Buffer> {
    try {
      const brotli = require('brotli');
      return Buffer.from(brotli.decompress(buffer));
    } catch (error) {
      console.warn(chalk.yellow('Brotli 解压缩不可用，回退到 Gzip'));
      return await this.decompressGzip(buffer);
    }
  }

  /**
   * 获取压缩统计信息
   */
  getCompressionStats(
    originalSizes: number[],
    compressedSizes: number[]
  ): {
    totalOriginalSize: number;
    totalCompressedSize: number;
    averageCompressionRatio: number;
    spaceSaved: number;
    spaceSavedPercentage: number;
  } {
    const totalOriginalSize = originalSizes.reduce(
      (sum, size) => sum + size,
      0
    );
    const totalCompressedSize = compressedSizes.reduce(
      (sum, size) => sum + size,
      0
    );
    const spaceSaved = totalOriginalSize - totalCompressedSize;
    const spaceSavedPercentage =
      totalOriginalSize > 0 ? (spaceSaved / totalOriginalSize) * 100 : 0;

    const compressionRatios = originalSizes.map((original, index) =>
      this.calculateCompressionRatio(original, compressedSizes[index])
    );
    const averageCompressionRatio =
      compressionRatios.reduce((sum, ratio) => sum + ratio, 0) /
      compressionRatios.length;

    return {
      totalOriginalSize,
      totalCompressedSize,
      averageCompressionRatio,
      spaceSaved,
      spaceSavedPercentage,
    };
  }
}
